import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { sectionApi, ContentField } from '../api/section.api';
import ContentEditor from './ContentEditor';
import Modal from './Modal';
import ProductImportExport from './ProductImportExport';
import { useNotify } from '../context/NotifyContext';

interface CategoryProductManagerProps {
  websiteId: string;
  section: any;
}

interface ProductModalState {
  mode: 'add' | 'edit';
  categoryId: string;
  product?: any;
}

const CategoryProductManager: React.FC<CategoryProductManagerProps> = ({ websiteId, section }) => {
  const queryClient = useQueryClient();
  const { notify, confirm } = useNotify();
  const [activeTab, setActiveTab] = useState<'categories' | 'products'>('categories');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categoryModal, setCategoryModal] = useState<{ mode: 'add' | 'edit'; category?: any } | null>(null);
  const [productModal, setProductModal] = useState<ProductModalState | null>(null);

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', websiteId, section._id],
    queryFn: () => websiteApi.getCategoriesForSection(websiteId, section._id),
  });

  // Fetch products
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products', websiteId, section._id, selectedCategoryId],
    queryFn: () => websiteApi.getProductsForCategory(websiteId, section._id, selectedCategoryId),
    enabled: activeTab === 'products',
  });

  // Fetch category schema
  const { data: categorySchema } = useQuery({
    queryKey: ['categorySchema', section.layoutId?.layoutId],
    queryFn: () => sectionApi.getContentSchema(section.layoutId?.layoutId),
    enabled: !!section.layoutId?.layoutId,
  });

  // Define product fields directly (not using layout schema as it may have array structure)
  const productFields: ContentField[] = [
    { name: 'name', label: 'Product Name', type: 'text', required: true, placeholder: 'e.g., Laptop' },
    { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Product description' },
    { name: 'price', label: 'Price', type: 'text', placeholder: 'e.g., $999' },
    { name: 'image', label: 'Product Image', type: 'image', recommendedSize: { width: 800, height: 600, note: '4:3' } },
    { name: 'features', label: 'Features', type: 'textarea', placeholder: 'Key features (optional)' },
  ];

  const invalidateCategoriesAndProducts = () => {
    queryClient.invalidateQueries({ queryKey: ['categories', websiteId, section._id] });
    queryClient.invalidateQueries({ queryKey: ['products', websiteId, section._id] });
  };

  // Add category mutation
  const addCategoryMutation = useMutation({
    mutationFn: (categoryData: any) => websiteApi.createContentItem(websiteId, section._id, categoryData),
    onSuccess: () => {
      invalidateCategoriesAndProducts();
      setCategoryModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to add category');
    },
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: ({ categoryId, categoryData }: { categoryId: string; categoryData: any }) =>
      websiteApi.updateContentItem(websiteId, section._id, categoryId, categoryData),
    onSuccess: () => {
      invalidateCategoriesAndProducts();
      setCategoryModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to update category');
    },
  });

  // Add product mutation
  const addProductMutation = useMutation({
    mutationFn: ({ categoryId, productData }: { categoryId: string; productData: any }) =>
      websiteApi.createProductWithCategory(websiteId, section._id, categoryId, productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', websiteId, section._id] });
      setProductModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to add product');
    },
  });

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: ({
      productId,
      categoryId,
      productData,
    }: {
      productId: string;
      categoryId: string;
      productData: any;
    }) => websiteApi.updateProduct(websiteId, section._id, productId, productData, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', websiteId, section._id] });
      setProductModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to update product');
    },
  });

  // Delete category mutation (cascades to its products server-side)
  const deleteCategoryMutation = useMutation({
    mutationFn: (categoryId: string) => websiteApi.deleteContentItem(websiteId, section._id, categoryId),
    onSuccess: () => {
      invalidateCategoriesAndProducts();
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete category');
    },
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: (productId: string) => websiteApi.deleteProduct(websiteId, section._id, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', websiteId, section._id] });
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete product');
    },
  });

  const openAddProductModal = () => {
    const defaultCategoryId = selectedCategoryId || categories[0]?._id || '';
    setProductModal({ mode: 'add', categoryId: defaultCategoryId });
  };

  const openEditProductModal = (product: any) => {
    const currentCategoryId = product.categoryId?._id || product.categoryId || '';
    setProductModal({ mode: 'edit', categoryId: currentCategoryId, product });
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex space-x-1 bg-nova-bg3 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'categories'
              ? 'bg-nova-bg3 text-nova-sky border border-nova-border'
              : 'text-nova-text2 hover:text-nova-text'
          }`}
        >
          Categories ({categories.length})
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'products'
              ? 'bg-nova-bg3 text-nova-sky border border-nova-border'
              : 'text-nova-text2 hover:text-nova-text'
          }`}
        >
          Products
        </button>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-nova-text">Categories ({categories.length})</h4>
            <button
              onClick={() => setCategoryModal({ mode: 'add' })}
              className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
            >
              + Add Category
            </button>
          </div>

          {categoriesLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category: any) => (
                <div
                  key={category._id}
                  className="p-4 border border-nova-border rounded-lg bg-nova-bg3 hover:border-nova-border2 transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {category.image && (
                        <img
                          src={category.image}
                          alt={category.categoryName}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <h5 className="font-semibold text-nova-text">{category.categoryName}</h5>
                      {category.description && (
                        <p className="text-sm text-nova-text2 mt-1">{category.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      <button
                        onClick={() => setCategoryModal({ mode: 'edit', category })}
                        className="p-2.5 text-nova-sky hover:bg-nova-sky/10 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete category',
                            message: 'Delete this category? Any products in this category will also be deleted.',
                            confirmLabel: 'Delete',
                            danger: true,
                          });
                          if (ok) deleteCategoryMutation.mutate(category._id);
                        }}
                        className="p-2.5 text-nova-red hover:bg-nova-red/10 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-nova-bg2 rounded-lg">
              <p className="text-nova-text3">No categories yet. Add your first category above.</p>
            </div>
          )}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          {categories.length === 0 ? (
            <div className="text-center py-12 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-400 font-medium">Please add categories first!</p>
              <button
                onClick={() => setActiveTab('categories')}
                className="mt-4 px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2"
              >
                Go to Categories
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-nova-text2 mb-2">Filter by Category</label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="w-full md:w-64 px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat: any) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                  <ProductImportExport
                    websiteId={websiteId}
                    sectionId={section._id}
                    mode="categorised"
                    label={section.title || 'catalogue'}
                    onImported={invalidateCategoriesAndProducts}
                  />
                  <button
                    onClick={openAddProductModal}
                    className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
                  >
                    + Add Product
                  </button>
                </div>
              </div>
              <p className="text-xs text-nova-text3">
                Export to edit your products in Excel or Google Sheets, then import the same file back. Keep a
                product's id to update it, or leave the id blank to add a new one. A category that does not exist
                yet is created on import.
              </p>

              {/* Existing Products */}
              {productsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
                </div>
              ) : products.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-nova-text mb-3">
                    Products ({products.length})
                    {selectedCategoryId && ` in ${categories.find((c: any) => c._id === selectedCategoryId)?.categoryName}`}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {products.map((product: any) => (
                      <div
                        key={product._id}
                        className="p-4 border border-nova-border rounded-lg bg-nova-bg3 hover:border-nova-border2 transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-nova-sky rounded">
                            {product.category || product.categoryId?.categoryName}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => openEditProductModal(product)}
                              className="p-2.5 -m-1 text-nova-sky hover:bg-nova-sky/10 rounded transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Delete product',
                                  message: 'Delete this product?',
                                  confirmLabel: 'Delete',
                                  danger: true,
                                });
                                if (ok) deleteProductMutation.mutate(product._id);
                              }}
                              className="p-2.5 -m-1 text-nova-red hover:bg-nova-red/10 rounded transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-32 object-cover rounded-lg mb-3"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/300x200?text=No+Image';
                            }}
                          />
                        )}
                        <h5 className="font-semibold text-nova-text">{product.name}</h5>
                        {product.description && (
                          <p className="text-sm text-nova-text2 mt-1 line-clamp-2">{product.description}</p>
                        )}
                        {product.price && (
                          <p className="text-lg font-bold text-nova-sky mt-2">{product.price}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-nova-bg2 rounded-lg">
                  <p className="text-nova-text3">
                    {selectedCategoryId
                      ? 'No products in this category yet.'
                      : 'No products yet. Add your first product above.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={!!categoryModal}
        onClose={() => setCategoryModal(null)}
        title={categoryModal?.mode === 'edit' ? 'Edit Category' : 'Add New Category'}
      >
        {categorySchema && categoryModal && (
          <ContentEditor
            key={categoryModal.category?._id || 'new-category'}
            fields={categorySchema.fields}
            initialContent={categoryModal.category || {}}
            onSave={(categoryData) =>
              categoryModal.mode === 'edit'
                ? updateCategoryMutation.mutate({ categoryId: categoryModal.category._id, categoryData })
                : addCategoryMutation.mutate(categoryData)
            }
            isSaving={addCategoryMutation.isPending || updateCategoryMutation.isPending}
          />
        )}
      </Modal>

      {/* Add/Edit Product Modal */}
      <Modal
        isOpen={!!productModal}
        onClose={() => setProductModal(null)}
        title={productModal?.mode === 'edit' ? 'Edit Product' : 'Add New Product'}
      >
        {productModal && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-nova-text2 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={productModal.categoryId}
                onChange={(e) => setProductModal({ ...productModal, categoryId: e.target.value })}
                className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
              >
                <option value="">-- Select Category --</option>
                {categories.map((cat: any) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            {productModal.categoryId && (
              <ContentEditor
                key={productModal.product?._id || 'new-product'}
                fields={productFields}
                initialContent={productModal.product || {}}
                onSave={(productData) =>
                  productModal.mode === 'edit'
                    ? updateProductMutation.mutate({
                        productId: productModal.product._id,
                        categoryId: productModal.categoryId,
                        productData,
                      })
                    : addProductMutation.mutate({ categoryId: productModal.categoryId, productData })
                }
                isSaving={addProductMutation.isPending || updateProductMutation.isPending}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CategoryProductManager;
